import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import { FirebaseContext } from 'common';
import { ref, onValue, update } from "firebase/database";
import {
  Box, Card, CardContent, Typography, Chip, Avatar, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, IconButton, Tooltip,
  Tabs, Tab
} from '@mui/material';
import { Search, EmojiEvents, CardGiftcard, CheckCircle, HourglassEmpty, TrendingUp } from '@mui/icons-material';
import { FONT_FAMILY, WEB_MAIN_COLOR, WEB_SECONDORY_COLOR } from "../common/sharedFunctions";
import moment from 'moment/min/moment-with-locales';
import PageSEO from '../components/SEO/PageSEO';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reward-league-tabpanel-${index}`}
      aria-labelledby={`reward-league-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function DriverRewardsAndLeague() {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const firebase = useContext(FirebaseContext);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [leagueConfig, setLeagueConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [tab, setTab] = useState(0);
  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);

  useEffect(() => {
    if (!firebase || !firebase.database) { setLoading(false); return; }
    let loaded = 0;
    const done = () => { loaded++; if (loaded >= 2) setLoading(false); };

    const leagueRef = ref(firebase.database, 'driver_league');
    const unsub1 = onValue(leagueRef, (snapshot) => {
      const val = snapshot.val();
      if (val) {
        const arr = Object.keys(val).map(driverId => {
          const data = val[driverId];
          const driver = staticusers ? staticusers.find(u => u.id === driverId) : null;
          return {
            id: driverId,
            driverName: driver ? `${driver.firstName} ${driver.lastName}` : (data.driverName || 'Unknown'),
            email: driver ? driver.email : '',
            mobile: driver ? driver.mobile : '',
            profileImage: driver ? driver.profile_image : null,
            xp: data.xp || 0,
            level: data.level || 'Bronze',
            totalOnlineHours: data.totalOnlineHours || 0,
            totalRides: data.totalRides || 0,
            acceptRate: data.acceptRate || 0,
            weeklyXP: data.weeklyXP || 0,
            rewards: data.rewards || {},
            lastUpdated: data.lastUpdated || null,
          };
        });
        arr.sort((a, b) => b.xp - a.xp);
        setDrivers(arr);
      } else {
        setDrivers([]);
      }
      done();
    }, () => done());

    const configRef = ref(firebase.database, 'league_config');
    const unsub2 = onValue(configRef, (snapshot) => {
      setLeagueConfig(snapshot.val());
      done();
    }, () => done());

    return () => { if (unsub1) unsub1(); if (unsub2) unsub2(); };
  }, [firebase, staticusers]);

  const getLevel = (xp) => {
    if (!leagueConfig || !leagueConfig.levels) return { name: 'Bronze', icon: '🥉', color: '#CD7F32' };
    const levels = [...leagueConfig.levels].sort((a, b) => b.minXP - a.minXP);
    for (let level of levels) {
      if (xp >= level.minXP) return level;
    }
    return leagueConfig.levels[0];
  };

  const getNextLevel = (xp) => {
    if (!leagueConfig || !leagueConfig.levels) return null;
    const levels = [...leagueConfig.levels].sort((a, b) => a.minXP - b.minXP);
    for (let level of levels) {
      if (xp < level.minXP) return level;
    }
    return null;
  };

  const getProgress = (xp) => {
    const currentLevel = getLevel(xp);
    const nextLevel = getNextLevel(xp);
    if (!nextLevel) return 100;
    const range = nextLevel.minXP - currentLevel.minXP;
    const progress = xp - currentLevel.minXP;
    return Math.min((progress / range) * 100, 100);
  };

  const updateRewardStatus = (driverId, rewardKey, newStatus) => {
    if (!firebase || !firebase.database) return;
    update(ref(firebase.database, `driver_league/${driverId}/rewards/${rewardKey}`), { status: newStatus });
  };

  const filteredDrivers = drivers.filter(d => {
    const matchesSearch = d.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = filterLevel === 'all' || d.level === filterLevel;
    return matchesSearch && matchesLevel;
  });

  const stats = {
    total: drivers.length,
    bronze: drivers.filter(d => getLevel(d.xp).name === 'Bronze').length,
    silver: drivers.filter(d => getLevel(d.xp).name === 'Silver').length,
    gold: drivers.filter(d => getLevel(d.xp).name === 'Gold').length,
    platinum: drivers.filter(d => getLevel(d.xp).name === 'Platinum').length,
    diamond: drivers.filter(d => getLevel(d.xp).name === 'Diamond').length,
  };

  if (loading) return <CircularLoading />;

  return (
    <Box sx={{ p: 2 }}>
      <PageSEO title="Driver Rewards & League" path="/driverrewardsandleague" noIndex={true} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('driver_rewards')} / {t('driver_league')}
        </Typography>
        <Tabs 
          value={tab} 
          onChange={(e, newValue) => setTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<EmojiEvents />} 
            iconPosition="start" 
            label={t('driver_rewards') || 'Driver Rewards'} 
            sx={{ fontWeight: tab === 0 ? 'bold' : 'normal' }}
          />
          <Tab 
            icon={<TrendingUp />} 
            iconPosition="start" 
            label={t('driver_league') || 'Driver League'} 
            sx={{ fontWeight: tab === 1 ? 'bold' : 'normal' }}
          />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        {/* Rewards Table */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('driver_rewards') || 'Driver Rewards'}
          </Typography>
          <RewardsTable firebase={firebase} staticusers={staticusers} settings={settings} />
        </Box>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Drivers', value: stats.total, color: MAIN_COLOR },
            { label: '🥉 Bronze', value: stats.bronze, color: '#CD7F32' },
            { label: '🥈 Silver', value: stats.silver, color: '#C0C0C0' },
            { label: '🥇 Gold', value: stats.gold, color: '#FFD700' },
            { label: '💎 Platinum', value: stats.platinum, color: '#E5E4E2' },
            { label: '👑 Diamond', value: stats.diamond, color: '#B9F2FF' },
          ].map((stat, idx) => (
            <Grid item xs={6} sm={4} md={2} key={idx}>
              <Card variant="outlined" sx={{ textAlign: 'center', borderColor: stat.color, borderWidth: 2, borderRadius: 2 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: stat.color }}>{stat.value}</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>{stat.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search drivers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter Level</InputLabel>
            <Select value={filterLevel} label="Filter Level" onChange={(e) => setFilterLevel(e.target.value)}>
              <MenuItem value="all">All Levels</MenuItem>
              {leagueConfig?.levels?.map((level, idx) => (
                <MenuItem key={idx} value={level.name}>{level.icon} {level.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Drivers Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Driver</strong></TableCell>
                <TableCell><strong>Level</strong></TableCell>
                <TableCell><strong>XP</strong></TableCell>
                <TableCell><strong>Progress</strong></TableCell>
                <TableCell><strong>Online Hours</strong></TableCell>
                <TableCell><strong>Rides</strong></TableCell>
                <TableCell><strong>Accept Rate</strong></TableCell>
                <TableCell><strong>Weekly XP</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <EmojiEvents sx={{ fontSize: 48, color: '#ddd', mb: 1 }} />
                    <Typography color="textSecondary">
                      {drivers.length === 0 ? 'No driver league data yet. The system will start tracking once drivers go online.' : 'No drivers match your filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrivers.map((driver, idx) => {
                  const level = getLevel(driver.xp);
                  const nextLevel = getNextLevel(driver.xp);
                  return (
                    <TableRow key={driver.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedDriver(driver)}>
                      <TableCell>
                        <Chip label={idx + 1} size="small" sx={{ fontWeight: 'bold', minWidth: 32 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={driver.profileImage} sx={{ width: 32, height: 32 }}>
                            {driver.driverName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{driver.driverName}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {settings?.AllowCriticalEditsAdmin ? driver.email : '***'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${level.icon} ${level.name}`}
                          size="small"
                          sx={{ backgroundColor: level.color + '30', fontWeight: 'bold', borderColor: level.color, borderWidth: 1 }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{driver.xp.toLocaleString()} XP</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Box>
                          <LinearProgress
                            variant="determinate"
                            value={getProgress(driver.xp)}
                            sx={{ height: 8, borderRadius: 4, backgroundColor: '#eee', '& .MuiLinearProgress-bar': { backgroundColor: level.color } }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {nextLevel ? `${nextLevel.minXP - driver.xp} XP to ${nextLevel.name}` : 'Max Level'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{driver.totalOnlineHours.toFixed(1)}h</TableCell>
                      <TableCell>{driver.totalRides}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${driver.acceptRate}%`}
                          size="small"
                          color={driver.acceptRate >= 95 ? 'success' : driver.acceptRate >= 80 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'green', fontWeight: 'bold' }}>+{driver.weeklyXP}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedDriver(driver); }}>
                            <CardGiftcard fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Driver Detail Dialog */}
        <Dialog open={!!selectedDriver} onClose={() => setSelectedDriver(null)} maxWidth="md" fullWidth>
          {selectedDriver && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={selectedDriver.profileImage} sx={{ width: 48, height: 48 }}>
                    {selectedDriver.driverName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedDriver.driverName}</Typography>
                    <Chip
                      label={`${getLevel(selectedDriver.xp).icon} ${getLevel(selectedDriver.xp).name} - ${selectedDriver.xp.toLocaleString()} XP`}
                      size="small"
                      sx={{ backgroundColor: getLevel(selectedDriver.xp).color + '30' }}
                    />
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent>
                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedDriver.totalOnlineHours.toFixed(1)}h</Typography>
                      <Typography variant="caption">Total Online</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedDriver.totalRides}</Typography>
                      <Typography variant="caption">Total Rides</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedDriver.acceptRate}%</Typography>
                      <Typography variant="caption">Accept Rate</Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Rewards */}
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Earned Rewards</Typography>
                {selectedDriver.rewards && Object.keys(selectedDriver.rewards).length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Reward</TableCell>
                          <TableCell>Level</TableCell>
                          <TableCell>Date Earned</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(selectedDriver.rewards).map(([key, reward]) => (
                          <TableRow key={key}>
                            <TableCell>{reward.description}</TableCell>
                            <TableCell><Chip label={reward.level} size="small" /></TableCell>
                            <TableCell>{reward.earnedAt ? moment(reward.earnedAt).format('lll') : '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={reward.status || 'pending'}
                                size="small"
                                color={reward.status === 'delivered' ? 'success' : reward.status === 'claimed' ? 'info' : 'warning'}
                                icon={reward.status === 'delivered' ? <CheckCircle /> : <HourglassEmpty />}
                              />
                            </TableCell>
                            <TableCell>
                              {reward.status !== 'delivered' && (
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <Select
                                    value={reward.status || 'pending'}
                                    onChange={(e) => updateRewardStatus(selectedDriver.id, key, e.target.value)}
                                    size="small"
                                  >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="claimed">Claimed</MenuItem>
                                    <MenuItem value="delivered">Delivered</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No rewards earned yet
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedDriver(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </TabPanel>
    </Box>
  );
}

function RewardsTable({ firebase, staticusers, settings }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebase || !firebase.database) { setLoading(false); return; }
    const rewardsRef = ref(firebase.database, 'driver_rewards');
    const unsubscribe = onValue(rewardsRef, (snapshot) => {
      const rewards = snapshot.val();
      if (rewards) {
        const arr = [];
        Object.keys(rewards).forEach(driverId => {
          const driverReward = rewards[driverId];
          const driver = staticusers ? staticusers.find(u => u.id === driverId) : null;
          const driverName = driver ? `${driver.firstName} ${driver.lastName}` : (driverReward.driverName || 'Unknown');
          const email = driver ? driver.email : '';
          const mobile = driver ? driver.mobile : '';
          const completedRides = driverReward.completedRides || 0;

          if (driverReward.milestones) {
            Object.keys(driverReward.milestones).forEach(mKey => {
              const m = driverReward.milestones[mKey];
              arr.push({
                id: `${driverId}_${mKey}`,
                driverId,
                milestoneKey: mKey,
                driverName,
                email,
                mobile,
                completedRides,
                rewardType: m.rewardType || 'uniform',
                description: m.description || '',
                value: m.value || '',
                ridesRequired: m.ridesRequired || 0,
                status: m.status || 'pending',
                earnedAt: m.earnedAt || null
              });
            });
          } else {
            arr.push({
              id: driverId,
              driverId,
              milestoneKey: null,
              driverName,
              email,
              mobile,
              completedRides,
              rewardType: '-',
              description: 'No milestone earned yet',
              value: '-',
              ridesRequired: '-',
              status: 'in_progress',
              earnedAt: null
            });
          }
        });
        setData(arr);
      } else {
        setData([]);
      }
      setLoading(false);
    }, () => { setData([]); setLoading(false); });
    return () => unsubscribe();
  }, [firebase, staticusers]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'claimed': return 'info';
      case 'shipped': return 'primary';
      case 'delivered': return 'success';
      case 'completed': return 'success';
      case 'in_progress': return 'default';
      default: return 'default';
    }
  };

  const columns = [
    { title: t('driver_name') || 'Driver', field: 'driverName' },
    { title: t('email') || 'Email', field: 'email', render: rowData => settings.AllowCriticalEditsAdmin ? rowData.email : t("hidden_demo") },
    { title: t('completed_rides') || 'Rides', field: 'completedRides', type: 'numeric' },
    { title: t('rides_required') || 'Milestone', field: 'ridesRequired' },
    {
      title: t('reward_type') || 'Type', field: 'rewardType',
      render: rowData => <Chip label={rowData.rewardType} size="small" variant="outlined" />
    },
    { title: t('description') || 'Reward', field: 'description' },
    { title: t('value') || 'Value', field: 'value', render: rowData => rowData.rewardType === 'cash' ? `${settings?.symbol || '$'}${rowData.value}` : rowData.value },
    {
      title: t('reward_status') || 'Status', field: 'status',
      render: rowData => (
        <Chip label={rowData.status} color={getStatusColor(rowData.status)} size="small" />
      ),
      lookup: { pending: 'Pending', claimed: 'Claimed', shipped: 'Shipped', delivered: 'Delivered', completed: 'Completed' }
    },
    {
      title: t('createdAt') || 'Earned At', field: 'earnedAt',
      render: rowData => rowData.earnedAt ? moment(rowData.earnedAt).format('lll') : '-',
      editable: 'never'
    },
  ];

  return loading ? <CircularLoading /> : (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
            <TableCell><strong>Driver</strong></TableCell>
            <TableCell><strong>Rides</strong></TableCell>
            <TableCell><strong>Milestone</strong></TableCell>
            <TableCell><strong>Type</strong></TableCell>
            <TableCell><strong>Reward</strong></TableCell>
            <TableCell><strong>Value</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Earned At</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(row => (
            <TableRow key={row.id}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.driverName}</Typography>
                {settings?.AllowCriticalEditsAdmin && (
                  <Typography variant="caption" color="textSecondary">{row.email}</Typography>
                )}
              </TableCell>
              <TableCell>{row.completedRides}</TableCell>
              <TableCell>{row.ridesRequired}</TableCell>
              <TableCell><Chip label={row.rewardType} size="small" variant="outlined" /></TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.rewardType === 'cash' ? `${settings?.symbol || '$'}${row.value}` : row.value}</TableCell>
              <TableCell>
                <Chip label={row.status} color={getStatusColor(row.status)} size="small" />
              </TableCell>
              <TableCell>{row.earnedAt ? moment(row.earnedAt).format('lll') : '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
